/**
 * GMB Locations API
 * 
 * Handles location discovery and management
 */

import { callGMBWorker } from './auth'
import type { GMBWorkerResponse, SyncResult } from '../types'

/**
 * Discover all GMB accounts and locations
 */
export async function discoverAllAccounts(): Promise<GMBWorkerResponse<SyncResult>> {
    return callGMBWorker<SyncResult>('discover_all')
}

/**
 * List locations for a specific account
 */
export async function listLocations(accountId: string): Promise<GMBWorkerResponse<{
    locations: Array<{
        name: string
        locationName: string
        primaryPhone?: string
        websiteUri?: string
        address?: any
        latlng?: {
            latitude: number
            longitude: number
        }
        placeId?: string
    }>
}>> {
    return callGMBWorker('list_locations', { account_id: accountId })
}

/**
 * Map GMB location to franchise region based on business rules
 * Toronto locations map to North York, all others map to Hamilton
 */
export function mapLocationToFranchise(locationName: string, address?: string): string {
    if (!locationName) {
        console.warn('mapLocationToFranchise called with undefined/null locationName')
        return 'hamilton' // Default fallback
    }
    const locationLower = locationName.toLowerCase()
    const addressLower = (address || '').toLowerCase()

    // Check if it's a Toronto location
    if (locationLower.includes('toronto') || addressLower.includes('toronto')) {
        return 'north_york'
    }

    // All other locations default to Hamilton
    return 'hamilton'
}

/**
 * Format GMB location for display
 */
export function formatGMBLocation(location: any) {
    return {
        id: location.name,
        name: location.locationName || 'Unknown Location',
        address: location.address ?
            [
                location.address.addressLines?.join(', '),
                location.address.locality,
                location.address.administrativeArea,
                location.address.postalCode
            ].filter(Boolean).join(', ') :
            'No address available',
        phone: location.primaryPhone,
        website: location.websiteUri,
        placeId: location.placeId,
        coordinates: location.latlng
    }
}
